import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Waves } from '@phosphor-icons/react';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
      else setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 relative overflow-hidden" style={{
      background: 'linear-gradient(135deg, #0a1f3d 0%, #0d3b66 50%, #1976d2 100%)'
    }}>
      {/* Ocean visual side */}
      <div className="hidden lg:flex relative items-end p-12">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Floating bubbles / wave shapes */}
          <div className="absolute top-1/4 left-1/4 w-72 h-72 rounded-full opacity-40 wave-float" style={{background: 'radial-gradient(circle, #06aed5 0%, transparent 70%)'}}></div>
          <div className="absolute bottom-1/3 right-1/4 w-96 h-96 rounded-full opacity-30 wave-float" style={{background: 'radial-gradient(circle, #4ea8de 0%, transparent 70%)', animationDelay: '2s'}}></div>
          <div className="absolute top-1/2 left-1/2 w-56 h-56 rounded-full opacity-35 wave-float" style={{background: 'radial-gradient(circle, #1976d2 0%, transparent 70%)', animationDelay: '4s'}}></div>
        </div>
        
        <div className="relative z-10 text-white max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-xl border border-white/30 flex items-center justify-center text-3xl font-bold" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>S</div>
            <div>
              <h1 className="text-3xl font-bold text-white" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>SAIL Books</h1>
              <p className="text-xs uppercase tracking-widest text-cyan-200/80 font-semibold">South African Information Logistics</p>
            </div>
          </div>
          
          <h2 className="text-5xl font-bold leading-tight mb-4 text-white" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
            Smooth sails<br/>for your<br/>business books
          </h2>
          <p className="text-lg text-cyan-100/90 leading-relaxed">
            All-in-one bookkeeping, compliance and CRM platform built for South African SMEs. Navigate SARS, BEE, CIPC and your finances with confidence.
          </p>
          
          <div className="mt-12 flex items-center gap-2 text-cyan-200/70 text-sm">
            <Waves size={20} weight="fill" />
            <span>Powered by intelligent automation</span>
          </div>
        </div>
      </div>
      
      {/* Form side */}
      <div className="flex items-center justify-center p-8 relative">
        <div className="w-full max-w-md glass-card p-10">
          <div className="mb-8">
            <div className="lg:hidden flex items-center gap-2.5 mb-6">
              <div className="w-10 h-10 rounded-lg ocean-gradient flex items-center justify-center text-white font-bold text-xl" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>S</div>
              <h1 className="text-2xl font-bold ocean-text" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>SAIL Books</h1>
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Welcome aboard</h2>
            <p className="mt-2 text-slate-600">Sign in to continue your journey</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div data-testid="login-error" className="p-3 rounded-lg border border-red-200 bg-red-50/80 backdrop-blur-sm text-red-800 text-sm">
                {error}
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-widest text-slate-600">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="mt-2 bg-white/60 border-slate-300 focus:border-blue-700 focus:ring-blue-200 transition-all"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-bold uppercase tracking-widest text-slate-600">Password</Label>
                <Link to="/forgot-password" className="text-xs text-blue-700 hover:underline font-semibold" data-testid="forgot-password-link">Forgot?</Link>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="mt-2 bg-white/60 border-slate-300 focus:border-blue-700 focus:ring-blue-200 transition-all"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full ocean-gradient text-white hover:opacity-90 h-12 font-semibold rounded-lg shadow-lg transition-all"
            >
              {loading ? 'Setting sail...' : 'Sign in'}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-600">
            New to SAIL Books?{' '}
            <Link to="/register" className="font-semibold text-blue-700 hover:underline" data-testid="go-to-register-link">
              Start your journey
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
